import React, { useEffect } from 'react'
import { useTheme } from '@mui/material'
import { makeStyles } from '@mui/styles'
import { Property } from 'csstype'

import { SHADOW_DOM_ZOOM_VALUE } from '../../constants/htmlPreviewParams'
import { ClassesType } from '../../types'

const useStyles = makeStyles(
  () => ({
    previewContainer: {
      zoom: ({ zoom }: UseStylesProps) => `${zoom}%`,
      '&::-webkit-scrollbar': {
        '-moz-appearance': 'none',
        '-webkit-appearance': 'none',
      },
      pointerEvents: ({ pointerEvents }: UseStylesProps) => pointerEvents,
    },
  }),
  { name: 'HtmlPreviewShadowDom' },
)

export interface HtmlPreviewShadowDomProps {
  readonly allowPointerEvents?: boolean
  readonly classes?: ClassesType<typeof useStyles>
  readonly css?: string
  readonly fontSize?: string
  readonly html?: string | null
  readonly zoom?: number
}

interface UseStylesProps {
  classes?: ClassesType<typeof useStyles>
  pointerEvents: Property.PointerEvents
  zoom: number
}

export const HtmlPreviewShadowDom = ({
  css,
  html,
  zoom = SHADOW_DOM_ZOOM_VALUE,
  classes: classesProp,
  allowPointerEvents = false,
  fontSize = '1.6rem',
}: HtmlPreviewShadowDomProps) => {
  const useStylesProps: UseStylesProps = {
    classes: classesProp || {},
    zoom,
    pointerEvents: allowPointerEvents ? 'auto' : 'none',
  }
  const classes = useStyles(useStylesProps)
  const theme = useTheme()

  const defaultCss = `
    div {
      color: ${theme.colors.secondaryText};
      font-size: ${fontSize};
    }
    p {
      margin: 0;
      color: ${theme.colors.secondaryText};
      font-size: ${fontSize};
    }
    .BUTTON {
      margin: 0 auto;
      display: block;
      text-decoration: none;
      color: ${theme.colors.profileText};
      background-color: ${theme.colors.title};
      border-radius: 23.5px;
      height: 44px;
      line-height: 44px;
      font-weight: bold;
      width: fit-content;
      padding: 0 ${theme.spacing(2)};
    }
    img {
      object-fit: cover;
      width: 100%;
      height: auto;
    }
    ul.todo-list {
      padding-left: ${theme.spacing(2)};
      list-style-type: none;
    }
    pre {
      white-space: normal;
    } 
  `

  const containerRef = React.createRef<HTMLDivElement>()

  useEffect(() => {
    const { current: container } = containerRef

    if (container && html) {
      if (!container.shadowRoot) {
        container.attachShadow({ mode: 'open' })
      }

      if (container.shadowRoot) {
        container.shadowRoot.innerHTML = ''

        const defaultStyle = document.createElement('style')
        defaultStyle.innerHTML = defaultCss
        container.shadowRoot.appendChild(defaultStyle)

        if (css) {
          const customStyle = document.createElement('style')
          customStyle.innerHTML = css
          container.shadowRoot.appendChild(customStyle)
        }

        if (html) {
          const content = document.createElement('div')
          content.innerHTML = html
          container.shadowRoot.appendChild(content)
        }
      }
    }
  }, [html])

  return <div className={classes.previewContainer} ref={containerRef} />
}
